/* eslint-disable react/jsx-props-no-spreading */

import React, { ReactElement, ReactNode, useMemo, useState } from "react";
import "./style.module.css";
import {
  FloatingPortal,
  Placement,
  autoPlacement,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useTransitionStyles,
} from "@floating-ui/react";

function Popover({
  trigger,
  children,
  content: floatingElement,
  placement: alignment,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(10),
      autoPlacement({
        allowedPlacements: alignment,
      }),
    ],
  });

  const hoverDisable = useMemo(() => {
    if (refs.domReference.current) {
      const getTrigger =
        refs.domReference.current.getAttribute("data-status") === "click";
      const openedState =
        refs.domReference.current.getAttribute("data-is-open") === "true";

      return getTrigger && openedState;
    }
    return false;
  }, [isOpen, refs.domReference.current]);

  const { isMounted, styles } = useTransitionStyles(context, {
    duration: {
      open: trigger === "hover" ? 600 : 200,
      close: 100,
    },
  });

  const click = useClick(context, {
    enabled: trigger === "click",
    ignoreMouse: trigger === "hover",
    event: "click",
  });

  const hover = useHover(context, {
    enabled: trigger === "hover" && !hoverDisable,
    delay: 200,
    move: true,
  });

  const dismiss = useDismiss(context, {
    referencePressEvent: "pointerdown",
    bubbles: true,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    trigger === "click" ? click : hover,
    dismiss,
  ]);

  return (
    <>
      <div
        id="reference"
        ref={refs.setReference}
        {...getReferenceProps()}
        data-status={trigger}
        data-is-open={isOpen}
      >
        {children}
      </div>
      {isOpen && isMounted && (
        <FloatingPortal>
          <div
            id="floating"
            data-placement={context.placement}
            data-trigger={trigger}
            ref={refs.setFloating}
            style={{
              ...styles,
              ...floatingStyles,
              zIndex: 100,
            }}
            {...getFloatingProps()}
          >
            <div
              style={{
                borderRadius: "0.625rem",
                backgroundColor: "#1a1a1a",
                display: "flex",
                flexDirection: "column",
                alignContent: "center",
              }}
            >
              {floatingElement}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

Popover.defaultProps = {
  trigger: "click",
  placement: "bottom",
};

export default Popover;
